class SideCart {
  constructor(freeShippingThreshold) {
    this.freeShippingThreshold = freeShippingThreshold || 0;
    this.cart = null;
    this.isOpen = false;
    this.isUpdating = false;

    this.el = null;
    this.overlay = null;
    this.closeButtons = [];
    this.headerCountEl = null;
    this.badgeElements = [];
    this.itemsContainer = null;
    this.emptyEl = null;
    this.filledElements = [];
    this.emptyElements = [];
    this.shippingEl = null;
    this.shippingTextEl = null;
    this.shippingFillEl = null;
    this.totalEl = null;

    this.init();
  }

  init() {
    this.el = document.getElementById('side-cart');
    if (!this.el) return;

    this.overlay        = this.el.querySelector('[data-side-cart-overlay]');
    this.closeButtons   = Array.from(this.el.querySelectorAll('[data-side-cart-close]'));
    this.headerCountEl  = this.el.querySelector('[data-side-cart-header-count]');
    this.itemsContainer = this.el.querySelector('[data-side-cart-items]');
    this.emptyEl        = this.el.querySelector('[data-side-cart-empty]');
    this.shippingTextEl = this.el.querySelector('[data-side-cart-shipping-text]');
    this.shippingFillEl = this.el.querySelector('[data-side-cart-shipping-fill]');
    this.totalEl        = this.el.querySelector('[data-side-cart-total]');
    this.filledElements = Array.from(this.el.querySelectorAll('[data-side-cart-if-filled]'));
    this.emptyElements  = Array.from(this.el.querySelectorAll('[data-side-cart-if-empty]'));
    this.badgeElements  = Array.from(document.querySelectorAll('[data-side-cart-badge]'));

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }
    this.closeButtons.forEach((btn) => btn.addEventListener('click', () => this.close()));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });

    document.querySelectorAll('[data-side-cart-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => this.open());
    });

    document.addEventListener('side-cart:open', () => this.open());

    document.addEventListener('cart:updated', () => this.fetchAndRender());
    document.addEventListener('cart:build', () => this.fetchAndRender());
    document.addEventListener('ajaxProduct:added', () => {
      this.fetchAndRender();
      this.open();
    });

    this.el.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-side-cart-upsell-add]');
      if (btn) this.addUpsellToCart(btn);
    });

    const checkoutBtn = this.el.querySelector('.side-cart__checkout-btn');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', () => this.fireBeginCheckout());
    }

    this.fetchAndRender();

    if (document.readyState === 'complete') {
      this.initUpsellsSwiper();
    } else {
      window.addEventListener('load', () => this.initUpsellsSwiper());
    }
  }

  initUpsellsSwiper() {
    const swiperEl = this.el.querySelector('.side-cart-upsells-swiper');
    if (!swiperEl || typeof Swiper === 'undefined' || this.upsellsSwiper) return;

    this.upsellsSwiper = new Swiper(swiperEl, {
      slidesPerView: 1.25,
      spaceBetween: 12,
      grabCursor: true,
      navigation: {
        prevEl: '.swiper-button-prev-side-cart',
        nextEl: '.swiper-button-next-side-cart',
      },
    });
  }

  open() {
    this.el.classList.add('is-open');
    this.el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('side-cart-open');
    this.isOpen = true;
    // Always refresh on open in case cart changed on another tab or via other UI
    this.fetchAndRender();
  }

  close() {
    this.el.classList.remove('is-open');
    this.el.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('side-cart-open');
    this.isOpen = false;
  }

  fetchAndRender() {
    fetch(theme.routes.cart)
      .then((res) => res.json())
      .then((cart) => {
        this.cart = cart;
        this.render();
      });
  }

  render() {
    const isEmpty = this.cart.item_count === 0;

    // Header count
    if (this.headerCountEl) {
      this.headerCountEl.textContent = isEmpty ? '' : `\u2022 ${this.cart.item_count}`;
    }

    // Trigger badges
    this.badgeElements.forEach((el) => {
      el.textContent = this.cart.item_count;
      el.classList.toggle('is-hidden', isEmpty);
    });

    // Cart total
    if (this.totalEl) {
      this.totalEl.textContent = `\u2022 ${this.formatMoney(this.cart.total_price)}`;
    }

    // Show/hide state-dependent elements
    this.filledElements.forEach((el) => el.classList.toggle('is-hidden', isEmpty));
    this.emptyElements.forEach((el) => el.classList.toggle('is-hidden', !isEmpty));

    // Shipping progress
    this.updateShippingProgress();

    // Items
    this.renderItems();

    if (this.isOpen && !isEmpty) this.fireViewCart();
  }

  updateShippingProgress() {
    if (!this.shippingTextEl || !this.shippingFillEl) return;

    const value     = this.cart.items_subtotal_price;
    const threshold = this.freeShippingThreshold;

    if (value >= threshold) {
      this.shippingTextEl.textContent = 'Du hast kostenlosen Versand erreicht! \uD83C\uDF89';
      this.shippingFillEl.style.width = '100%';
      const truck = this.el.querySelector('[data-side-cart-shipping-truck]');
      if (truck) truck.classList.add('is-reached');
    } else {
      const remaining = threshold - value;
      this.shippingTextEl.textContent = `Nur noch ${this.formatMoney(remaining)} bis zum Gratis Versand!`;
      const pct = Math.min((value / threshold) * 100, 99);
      this.shippingFillEl.style.width = `${pct}%`;
      const truck = this.el.querySelector('[data-side-cart-shipping-truck]');
      if (truck) truck.classList.remove('is-reached');
    }
  }

  renderItems() {
    if (!this.itemsContainer) return;

    // Remove previously rendered items (keep empty state el)
    Array.from(this.itemsContainer.children).forEach((child) => {
      if (!child.hasAttribute('data-side-cart-empty')) child.remove();
    });

    if (this.cart.item_count === 0) {
      if (this.emptyEl) this.emptyEl.classList.remove('is-hidden');
      return;
    }

    if (this.emptyEl) this.emptyEl.classList.add('is-hidden');

    this.cart.items.forEach((item) => {
      this.itemsContainer.appendChild(this.buildItemEl(item));
    });
  }

  buildItemEl(item) {
    const isSample = item.properties && item.properties.__free_sample === 'true';

    const el = document.createElement('div');
    el.className = 'side-cart__item';
    el.dataset.key = item.key;
    if (isSample) el.dataset.sample = 'true';

    const imgSrc = item.featured_image && item.featured_image.url
      ? this.getSizedImageUrl(item.featured_image.url, '180x180_crop_center')
      : (item.image ? this.getSizedImageUrl(item.image, '180x180_crop_center') : '');

    const imgHtml = imgSrc
      ? `<img src="${imgSrc}" alt="${this.escapeHtml(item.product_title)}" width="90" height="90" loading="lazy">`
      : '<div class="side-cart__item-img-placeholder"></div>';

    const variantHtml = item.variant_title && item.variant_title !== 'Default Title'
      ? `<div class="side-cart__item-variant">${this.escapeHtml(item.variant_title)}</div>`
      : '';

    const priceHtml = item.original_line_price !== item.final_line_price
      ? `<s class="side-cart__item-price side-cart__item-price--original">${this.formatMoney(item.original_price)}</s>
         <span class="side-cart__item-price side-cart__item-price--sale">${this.formatMoney(item.final_price)}</span>`
      : `<span class="side-cart__item-price">${this.formatMoney(item.final_price)}</span>`;

    const qtyHtml = isSample
      ? `<span class="side-cart__item-sample-label">Gratis-Probe</span>`
      : `<div class="side-cart__item-qty">
          <button type="button" class="side-cart__qty-btn" data-action="decrease" aria-label="Menge verringern">
            <svg width="10" height="2" viewBox="0 0 10 2" fill="none" aria-hidden="true"><line x1="0" y1="1" x2="10" y2="1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </button>
          <span class="side-cart__qty-num">${item.quantity}</span>
          <button type="button" class="side-cart__qty-btn" data-action="increase" aria-label="Menge erhöhen">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><line x1="5" y1="0" x2="5" y2="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="0" y1="5" x2="10" y2="5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </button>
        </div>`;

    const removeHtml = `
      <button type="button" class="side-cart__item-remove" aria-label="Artikel entfernen">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
        </svg>
      </button>`;

    el.innerHTML = `
      <a href="${this.escapeHtml(item.url)}" class="side-cart__item-img-wrap">${imgHtml}</a>
      <div class="side-cart__item-details">
        <div class="side-cart__item-top">
          <a href="${this.escapeHtml(item.url)}" class="side-cart__item-title">${this.escapeHtml(item.product_title)}</a>
          ${removeHtml}
        </div>
        ${variantHtml}
        <div class="side-cart__item-bottom">
          ${qtyHtml}
          <div class="side-cart__item-price-wrap">${priceHtml}</div>
        </div>
      </div>
    `;

    // Bind qty buttons
    el.querySelectorAll('[data-action]').forEach((btn) => {
      btn.addEventListener('click', () => this.changeQuantity(item, btn.dataset.action));
    });

    // Bind remove button
    const removeBtn = el.querySelector('.side-cart__item-remove');
    if (removeBtn) {
      removeBtn.addEventListener('click', () => this.updateItem(item.key, 0));
    }

    return el;
  }

  changeQuantity(item, action) {
    const newQty = action === 'increase' ? item.quantity + 1 : item.quantity - 1;
    if (newQty < 0) return;
    this.updateItem(item.key, newQty);
  }

  addUpsellToCart(btn) {
    const variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId) return;

    btn.disabled = true;

    fetch(theme.routes.cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.items && data.items.length) {
          const added = data.items[0];
          this.fireAddToCart(added.variant_id, added.product_title, added.final_price, 1);
        }
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('cart:updated'));
      })
      .finally(() => {
        btn.disabled = false;
      });
  }

  updateItem(key, quantity) {
    if (this.isUpdating) return;
    this.isUpdating = true;

    fetch(theme.routes.cartChange, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    })
      .then((res) => res.json())
      .then((cart) => {
        this.cart = cart;
        this.render();
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('cart:updated'));
      })
      .finally(() => {
        this.isUpdating = false;
      });
  }

  getSizedImageUrl(url, size) {
    if (!url) return '';
    return url.replace(/(\.\w+)(\?.*)?$/, `_${size}$1$2`);
  }

  formatMoney(amount) {
    if (window.theme && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(amount, false);
    }
    const format = (window.theme && window.theme.settings && window.theme.settings.moneyFormat)
      ? window.theme.settings.moneyFormat
      : '{{amount}}';
    if (window.Shopify && Shopify.formatMoney) {
      return Shopify.formatMoney(amount, format);
    }
    return `${(amount / 100).toFixed(2)}\u00A0\u20AC`;
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str || ''));
    return div.innerHTML;
  }

  gtagCurrency() {
    return (window.Shopify && Shopify.currency && Shopify.currency.active) ? Shopify.currency.active : 'EUR';
  }

  cartItemsForGtag() {
    if (!this.cart || !this.cart.items) return [];
    return this.cart.items.map((item, index) => ({
      item_id: String(item.variant_id),
      item_name: item.product_title,
      item_variant: item.variant_title !== 'Default Title' ? item.variant_title : undefined,
      price: item.final_price / 100,
      quantity: item.quantity,
      index,
    }));
  }

  fireViewCart() {
    if (typeof gtag === 'undefined' || !this.cart) return;
    gtag('event', 'view_cart', {
      currency: this.gtagCurrency(),
      value: this.cart.total_price / 100,
      items: this.cartItemsForGtag(),
    });
  }

  fireBeginCheckout() {
    if (typeof gtag === 'undefined' || !this.cart) return;
    gtag('event', 'begin_checkout', {
      currency: this.gtagCurrency(),
      value: this.cart.total_price / 100,
      items: this.cartItemsForGtag(),
    });
  }

  fireAddToCart(variantId, productTitle, finalPrice, quantity) {
    if (typeof gtag === 'undefined') return;
    gtag('event', 'add_to_cart', {
      currency: this.gtagCurrency(),
      value: (finalPrice / 100) * quantity,
      items: [{
        item_id: String(variantId),
        item_name: productTitle,
        price: finalPrice / 100,
        quantity,
      }],
    });
  }
}
